import React, {
    useState,
    useEffect,
    useRef,
    Fragment,
} from "react";
import { Row, Col, Input } from "reactstrap";
import { toast } from "react-toastify";
import io from "socket.io-client";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { requestApi, requestFormData } from "../../../request-api";
import { userStore } from "../../../stores";
import { API_URL } from '../../../config';

function Comments() {
    const socket = useRef();
    const messagesEndRef = useRef(null);

    const [companies, setCompanies] = useState([]);
    const [messages, setMessages] = useState([]);
    const [textMessage, setTextMessage] = useState('');
    const [files, setFiles] = useState(null);
    const [currentRoom, setCurrentRoom] = useState('');
    const [chatId, setChatId] = useState('');
    const [userId, setUserId] = useState(null);
    const [receiverId, setReceiverId] = useState(null);
    const [auditor, setAuditor] = useState('');
    const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [messageInput, setMessageInput] = useState('');
    const [messageId, setMessageId] = useState(null);
    const [query, setQuery] = useState('');
    const [companyName, setCompanyName] = useState({
        company: "",
        branch: ""
    });

    const fileInputRef = useRef();

    useEffect(() => {
        fetchCompanies();
        const rawAuditor = localStorage.getItem("name");
        const _auditor = rawAuditor?.replace(/^"|"$/g, '');
        const _userId = localStorage.getItem("user_id");

        setAuditor(_auditor);
        setUserId(Number(_userId));
    }, []);

    useEffect(() => {
        if (!userId) return;

        socket.current = io(API_URL);

        socket.current.on("connect", () => {
            console.log("✅ Connected to Socket.io", socket.current.id);
            console.log("userId on connect", userId);

            socket.current.emit("setUser", { id: userId });

            if (currentRoom) {
                socket.current.emit("join chat", currentRoom);
                console.log("🛜 Joined chat room:", currentRoom);
            }
        });

        socket.current.on("connected", (uid) => {
            console.log("✅ Connected:", uid);
        });

        socket.current.on("messageCreated", (newMessage) => {
            console.log("📩 New message received:", newMessage);
            setMessages((prev) => [...prev, newMessage]);
            setMessageId(newMessage.id);
        });


        socket.current.on("disconnect", () => {
            console.log("🔌 Disconnected from socket");
        });

        return () => {
            socket.current.disconnect();
        };
    }, [userId, currentRoom]);


    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const toggleDropdown = (index) => {
        setOpenDropdownIndex((prevIndex) => (prevIndex === index ? null : index));
    };

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            const res = await requestApi(`/auditor/company/getComapanyWithBranch`, {
                method: 'get'
            });
            setCompanies(res.data);
        } catch (err) {
            toast.error("Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    const branchHandler = (e, data) => {
        e.preventDefault();

        try {
            getMessages(e, {
                id: data.branch_id,
                receiver_id: userId
            });

            setCompanyName((prev) => ({
                ...prev,
                company: data.company_name,
                branch: data.branch_id
            }));
        } catch (err) {
            console.error("branch handler error", err);
        }
    };

    const getMessages = async (e, chatData) => {
        e.preventDefault();

        if (!chatData?.id) {
            toast.error("Invalid chat selected.");
            return;
        }

        try {
            setMessages([]);
            setTextMessage('');
            setChatId(chatData.id);
            setReceiverId(chatData.receiver_id);

            const res = await requestApi('/comment/getChatMessage', {
                method: 'post',
                body: JSON.stringify({
                    chat_id: chatData.id
                })
            });

            if (res.statusCode === 200) {
                const messages = res.data;
                socket.current.emit("joinRoom", chatData.id);
                console.log("🛜 Joined chat room:", chatData.id);
                setCurrentRoom(chatData.id);
                setMessages(messages);
            } else {
                toast.error("Failed to fetch messages.");
            }
        } catch (err) {
            toast.error("An error occurred while loading messages.");
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();

        if (!messageInput.trim() && !files) return;

        if (messageInput.trim()) {
            const messageData = {
                room_id: currentRoom,
                sender_id: userId,
                receiver_id: receiverId,
                message: messageInput.trim(),
                resolve: 0,
                urgent: 0,
                very_important: 0,
                request_for_doc: 0,
                chat_id: currentRoom,
            };

            socket.current.emit("createMessage", messageData, async (response) => {
                if (response.status === "ok") {
                    const newMessageId = response.messageId;
                    let uploadedFile = null;

                    if (files) {
                        uploadedFile = await handleCreateChatFile(newMessageId);
                    }

                    setMessages((prev) => [
                        ...prev,
                        {
                            ...messageData,
                            id: newMessageId,
                            file: uploadedFile || null,
                        },
                    ]);

                    setMessageInput("");
                    setMessageId(null);
                    setFiles(null);
                } else {
                    console.log("Error sending message:", response.error);
                }
            });
        }

        else if (files || !messageId) {
            const formData = new FormData();
            formData.append("file", files);
            formData.append("message_id", currentRoom);
            formData.append("file_id", companyName.branch);

            try {
                const res = await requestFormData("/comment/createCommentfile", formData);
                if (res.statusCode === 200) {
                    const fileMessage = {
                        id: res.data.message_id,
                        sender_id: userId,
                        receiver_id: receiverId,
                        chat_id: currentRoom,
                        message: "",
                        sender: res.data.sender,
                        file: res.data.file,
                        created_at: new Date().toISOString(),
                    };

                    socket.current.emit("createChatFile", fileMessage, (data) => {
                        console.log("📦 createChatFile response:", data);
                    });


                    setMessages((prev) => [...prev, fileMessage]);
                    setFiles(null);
                } else {
                    console.error("File-only upload failed");
                }
            } catch (err) {
                console.error("Upload failed", err);
            }
        }
    };


    const handleFileClick = () => {
        fileInputRef.current.click();
    };

    const handleCreateChatFile = async (message_id) => {
        const formData = new FormData();
        formData.append("file", files);
        formData.append("message_id", message_id);

        try {
            const response = await requestFormData('/comment/createCommentfile', formData);

            if (response.statusCode === 200) {
                return response.data.file;
            }
        } catch (error) {
            console.error("Upload error:", error);
        }

        return null;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            setFiles(file);
        }
    };

    const getDateGroupLabel = (date) => {
        if (!(date instanceof Date) || isNaN(date)) return "Unknown";

        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);

        const isSameDate = (d1, d2) =>
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();

        if (isSameDate(date, now)) return "Today";
        if (isSameDate(date, yesterday)) return "Yesterday";

        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return "";

        const d = new Date(timestamp);
        if (isNaN(d.getTime())) {
            return "";
        }

        let hours = d.getHours();
        const minutes = d.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;

        return `${hours}:${minutes} ${ampm}`;
    };

    const groupedMessages = {};

    messages.forEach((msg) => {
        if (!msg.created_at) return;

        const date = new Date(msg.created_at);
        if (isNaN(date)) return;

        const label = getDateGroupLabel(date);
        if (!groupedMessages[label]) {
            groupedMessages[label] = [];
        }
        groupedMessages[label].push(msg);
    });

    const extensionMap = {
        pdf: { type: 'PDF Document', icon: 'fas fa-file-pdf text-danger fa-lg' },
        doc: { type: 'Word Document', icon: 'fas fa-file-word text-primary fa-lg' },
        docx: { type: 'Word Document', icon: 'fas fa-file-word text-primary fa-lg' },
        xls: { type: 'Excel Spreadsheet', icon: 'fas fa-file-excel text-success fa-lg' },
        xlsx: { type: 'Excel Spreadsheet', icon: 'fas fa-file-excel text-success fa-lg' },
        ppt: { type: 'PowerPoint Presentation', icon: 'fas fa-file-powerpoint text-warning fa-lg' },
        pptx: { type: 'PowerPoint Presentation', icon: 'fas fa-file-powerpoint text-warning fa-lg' },
        txt: { type: 'Text File', icon: 'fas fa-file-alt text-secondary fa-lg' },
        zip: { type: 'ZIP Archive', icon: 'fas fa-file-archive text-muted fa-lg' },
        jpg: { type: 'Image File', icon: 'fas fa-file-image text-info fa-lg' },
        jpeg: { type: 'Image File', icon: 'fas fa-file-image text-info fa-lg' },
        png: { type: 'Image File', icon: 'fas fa-file-image text-info fa-lg' },
        gif: { type: 'Image File', icon: 'fas fa-file-image text-info fa-lg' },
    };

    function getExtension(filename) {
        return filename.split('.').pop().toLowerCase();
    }

    function isImageFile(filename) {
        const ext = getExtension(filename);
        return ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
    }

    function getFileType(filename) {
        const ext = getExtension(filename);
        return extensionMap[ext]?.type || 'File';
    }

    function renderFileIcon(filename) {
        const ext = getExtension(filename);
        const iconClass = extensionMap[ext]?.icon || 'fas fa-file text-dark fa-lg';
        return <i className={iconClass}></i>;
    }

    function cleanFileName(filename) {
        return filename.replace(/^uploads\//, '').replace(/^comments\//, '');
    }

    if (loading) {
        return (
            <div className="loader-container">
                <div className="spinner" />
            </div>
        );
    };

    return (
        <section className='lyt-section py-0'>
            <Row className="w-100  mt-8  comment-h">
                <Col md={3} className="bg-white  border p-4">
                    <div className="d-flex align-items-center border rounded  w-100">
                        <i className="bi bi-search"></i>
                        <input
                            type="text"
                            className="form-control fs-12 "
                            placeholder="Search..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <div className="d-flex flex-column gap-3 pt-4 overflow-auto" style={{ maxHeight: "600px" }}>




                        {companies
                            .filter((company) => company.company_name.toLowerCase().includes(query.toLowerCase()))
                            .map((company, index) => {
                                const { company_name, branches } = company;
                                const hasBranches = branches && branches.length > 0;

                                return (
                                    <Fragment key={index}>
                                        <div className="mb-2">
                                            <button
                                                onClick={() => toggleDropdown(index)}
                                                className={`custom-select-btn w-100 d-flex justify-content-between align-items-center
                                               ${openDropdownIndex === index ? "active" : ""}`}
                                            >
                                                <span className="typ-span">
                                                    {company_name}
                                                </span>

                                                {hasBranches && (
                                                    <span className="icon-wrapper">
                                                        {openDropdownIndex === index ? (
                                                            <FaChevronUp className="icon" />
                                                        ) : (
                                                            <FaChevronDown className="icon" />
                                                        )}
                                                    </span>
                                                )}
                                            </button>



                                            {openDropdownIndex === index && (
                                                <div className="border p-2 bg-light rounded fs-12">
                                                    {company.branches.map((branch) => {
                                                        const { branch_id } = branch;
                                                        return (
                                                            <Fragment key={branch.branch_id}>
                                                                <div
                                                                    onClick={(e) => branchHandler(e, { company_name, branch_id })}
                                                                    className="py-1 px-2 c-pointer hover:bg-primary hover:text-white rounded fs-12"
                                                                    style={{ cursor: "pointer" }}
                                                                >
                                                                    {branch_id}
                                                                </div>
                                                            </Fragment>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </Fragment>
                                )
                            })}
                    </div>
                </Col>

                <Col md={9} className="bg-light p-4 ">
                    <h6 className="fs-12">{companyName.company && `#${companyName.company}`}</h6>
                    <p className="border-bottom py-2 w-100 fs-12">{companyName.branch}</p>
                    {Object.entries(groupedMessages).map(([label, messages]) => {
                        return (
                            <div key={label} className="mb-4">
                                <div className="mb-2 d-flex justify-content-between fs-12">{label}</div>
                                {messages.map((key, index) => {
                                    return (
                                        <div key={index} className="d-flex align-items-center gap-3 mb-3">
                                            <img
                                                src="https://static.vecteezy.com/system/resources/previews/024/354/252/non_2x/businessman-isolated-illustration-ai-generative-free-photo.jpg"
                                                alt="avatar"
                                                className="img-fluid rounded-circle"
                                                style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                            />
                                            <div>
                                                <div className="d-flex gap-4">
                                                    <strong className="fs-12">{key.sender}</strong>
                                                    <small>{formatMessageTime(key.created_at)}</small>
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: '12px' }}>{key.message}</span>



                                                    {key.file && key.file !== "" && (
                                                        <div className="border p-2 rounded bg-light mt-2" style={{ fontSize: "12px", width: "300px" }}>
                                                            {!isImageFile(key.file) && (
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <div className="d-flex align-items-center">
                                                                        {renderFileIcon(key.file)}
                                                                        <div className="ml-2">
                                                                            <div><strong>{cleanFileName(key.file)}</strong></div>
                                                                            <div style={{ fontSize: "10px", color: "#888" }}>{getFileType(key.file)}</div>
                                                                        </div>
                                                                    </div>
                                                                    <a
                                                                        href={`${API_URL}/${key.file}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-sm btn-dark"
                                                                        style={{ fontSize: "10px" }}
                                                                    >
                                                                        View
                                                                    </a>
                                                                </div>
                                                            )}
                                                            {isImageFile(key.file) && (
                                                                <img
                                                                    src={`${API_URL}/${key.file}`}
                                                                    alt={key.file}
                                                                    style={{ width: "100%", maxHeight: "150px", objectFit: "contain", borderRadius: "8px", backgroundColor: "#f0f0f0" }}
                                                                    className="mt-2"
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    })}
                    <div ref={messagesEndRef} />


                </Col>
            </Row>
        </section >
    );
}

export default Comments;
